/**
 * Typed worker RPC (W1-13 persist-workers; contracted exports `createRpcClient`, `serveRpc`; workers/protocol.ts).
 *
 * Small and dependency-free (no comlink): requests carry ids, responses resolve or reject the matching call,
 * events flow from the worker to the client, and both directions accept transferables.
 *  - `call(method, params, transfer?)`: request id > 0; resolves with the handler's result.
 *  - `post(method, params, transfer?)`: fire-and-forget (request id 0, no response on success). A post whose
 *    handler fails is answered with an error naming the method; `createRpcClientEx` clients hear it through
 *    `onPostError` (a failed call, not a dead worker: without a listener it is dropped, as "no response" says).
 *  - Errors cross the boundary as `{ message, stack?, name?, code?, method? }`: `code` carries structured reasons
 *    such as the persist worker's PersistError codes ('lockedElsewhere', 'quota', ...). The client rejects with a
 *    `RemoteError` (an Error with the remote stack and `code`).
 *  - `onFailure`: worker `error` / `messageerror` events only, as the contract says (the persist client respawns
 *    the worker on them, so a single failing post must never reach it).
 *  - `terminate()`: terminates the worker and rejects every pending call; later calls reject immediately.
 *  - Results can be transferred instead of copied: a handler returns `withTransfer(value, [buffer])`.
 *
 * `connectInProcess()` builds a worker/scope pair that talks through structured clones on the same thread
 * (asynchronous, ordered per direction). Tests run real worker handlers with it; it is also how a module could
 * host a worker's handlers without a Worker.
 */

import type { Unsubscribe } from '../core/util/types';
import type {
  CreateRpcClient,
  RpcClient,
  RpcEventMsg,
  RpcHandlers,
  RpcRequestMsg,
  RpcResponseMsg,
  RpcScope,
  RpcServer,
  SchemaOf,
  ServeRpc,
} from './protocol';

/** Error payload on the wire (a superset of the protocol's `{ message, stack? }`). */
export interface RpcErrorPayload {
  readonly message: string;
  readonly stack?: string;
  readonly name?: string;
  readonly code?: string;
  /** The method of a failed fire-and-forget post (request id 0). */
  readonly method?: string;
}

/** A fire-and-forget post whose handler failed in the worker. */
export interface PostFailure {
  readonly method: string;
  readonly error: RemoteError;
}

/** The contract's client plus a channel for failed posts (kept apart from worker failures). */
export interface RpcClientEx<S extends SchemaOf<S>, E> extends RpcClient<S, E> {
  /** A `post` whose handler threw or rejected in the worker. The worker itself is fine (see `onFailure`). */
  onPostError(cb: (failure: PostFailure) => void): Unsubscribe;
}

/** Rejection of a call whose handler failed in the worker. */
export class RemoteError extends Error {
  readonly code: string | undefined;
  constructor(payload: RpcErrorPayload) {
    super(payload.message);
    this.name = payload.name ?? 'RemoteError';
    this.code = payload.code;
    if (payload.stack !== undefined) this.stack = payload.stack;
  }
}

/** Rejection of calls pending when the worker was terminated (or of calls made afterwards). */
export class RpcTerminatedError extends Error {
  override readonly name = 'RpcTerminatedError';
  constructor() {
    super('worker terminated');
  }
}

const TRANSFERS = new WeakMap<object, readonly Transferable[]>();

/** Marks a handler result (an object) to be transferred with the listed objects instead of copied. */
export function withTransfer<T extends object>(value: T, transfer: readonly Transferable[]): T {
  TRANSFERS.set(value, transfer);
  return value;
}

function transferOf(value: unknown): Transferable[] {
  if (typeof value !== 'object' || value === null) return [];
  const t = TRANSFERS.get(value);
  if (t === undefined) return [];
  TRANSFERS.delete(value);
  return [...t];
}

interface Pending {
  resolve(value: unknown): void;
  reject(error: Error): void;
}

function isObject(v: unknown): v is Readonly<Record<string, unknown>> {
  return typeof v === 'object' && v !== null;
}

function isResponse(v: unknown): v is RpcResponseMsg {
  return isObject(v) && v.t === 'res' && typeof v.id === 'number';
}

function isEvent(v: unknown): v is RpcEventMsg {
  return isObject(v) && v.t === 'evt' && typeof v.event === 'string';
}

function isRequest(v: unknown): v is RpcRequestMsg {
  return isObject(v) && v.t === 'req' && typeof v.id === 'number' && typeof v.method === 'string';
}

function errorPayload(err: unknown): RpcErrorPayload {
  if (err instanceof Error) {
    const code = (err as { code?: unknown }).code;
    return {
      message: err.message,
      name: err.name,
      ...(err.stack === undefined ? {} : { stack: err.stack }),
      ...(typeof code === 'string' ? { code } : {}),
    };
  }
  if (isObject(err) && typeof err.message === 'string') {
    return {
      message: err.message,
      ...(typeof err.code === 'string' ? { code: err.code } : {}),
      ...(typeof err.name === 'string' ? { name: err.name } : {}),
    };
  }
  return { message: String(err) };
}

/** Typed client over a worker (`createRpcClient` with the `onPostError` channel). */
export function createRpcClientEx<S extends SchemaOf<S>, E>(worker: Worker): RpcClientEx<S, E> {
  let nextId = 1;
  let terminated = false;
  const pending = new Map<number, Pending>();
  const listeners = new Map<string, Set<(payload: unknown) => void>>();
  const failureListeners = new Set<(reason: string) => void>();
  const postErrorListeners = new Set<(failure: PostFailure) => void>();

  const fail = (reason: string): void => {
    for (const cb of [...failureListeners]) cb(reason);
  };

  worker.addEventListener('message', (e: MessageEvent) => {
    const msg: unknown = e.data;
    if (isResponse(msg)) {
      if (msg.id === 0) {
        if (!msg.ok && !terminated) {
          const error = new RemoteError(msg.error);
          const method = msg.error.method ?? 'unknown';
          for (const cb of [...postErrorListeners]) cb({ method, error });
        }
        return;
      }
      const p = pending.get(msg.id);
      if (p === undefined) return;
      pending.delete(msg.id);
      if (msg.ok) p.resolve(msg.result);
      else p.reject(new RemoteError(msg.error));
    } else if (isEvent(msg)) {
      const set = listeners.get(msg.event);
      if (set !== undefined) for (const cb of [...set]) cb(msg.payload);
    }
  });
  worker.addEventListener('error', (e: ErrorEvent) => {
    fail(e.message || 'worker error');
  });
  worker.addEventListener('messageerror', () => {
    fail('worker messageerror');
  });

  const send = (id: number, method: string, params: unknown, transfer?: readonly Transferable[]): void => {
    const msg: RpcRequestMsg = { t: 'req', id, method, params };
    worker.postMessage(msg, transfer === undefined ? [] : [...transfer]);
  };

  return {
    call(method, params, transfer) {
      if (terminated) return Promise.reject(new RpcTerminatedError());
      const id = nextId++;
      return new Promise((resolve, reject) => {
        pending.set(id, { resolve, reject });
        try {
          send(id, method, params, transfer);
        } catch (err) {
          pending.delete(id);
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      });
    },
    post(method, params, transfer) {
      if (terminated) return;
      send(0, method, params, transfer);
    },
    on(event, cb) {
      let set = listeners.get(event);
      if (set === undefined) {
        set = new Set();
        listeners.set(event, set);
      }
      const fn = (payload: unknown): void => {
        cb(payload as E[typeof event]);
      };
      set.add(fn);
      return () => {
        set.delete(fn);
      };
    },
    onFailure(cb) {
      failureListeners.add(cb);
      return () => {
        failureListeners.delete(cb);
      };
    },
    onPostError(cb) {
      postErrorListeners.add(cb);
      return () => {
        postErrorListeners.delete(cb);
      };
    },
    terminate() {
      if (terminated) return;
      terminated = true;
      worker.terminate();
      const err = new RpcTerminatedError();
      for (const p of pending.values()) p.reject(err);
      pending.clear();
    },
  };
}

function rpcServer<S extends SchemaOf<S>, E>(scope: RpcScope, handlers: RpcHandlers<S>): RpcServer<E> {
  const table = handlers as unknown as Readonly<Record<string, ((params: unknown) => unknown) | undefined>>;
  const respond = (msg: RpcResponseMsg, transfer: Transferable[]): void => {
    scope.postMessage(msg, transfer);
  };
  const respondError = (id: number, method: string, err: unknown): void => {
    const payload = errorPayload(err);
    const error: RpcErrorPayload = id === 0 ? { ...payload, method } : payload;
    respond({ t: 'res', id, ok: false, error }, []);
  };
  scope.addEventListener('message', (e: MessageEvent) => {
    const msg: unknown = e.data;
    if (!isRequest(msg)) return;
    const { id, method, params } = msg;
    const handler = Object.prototype.hasOwnProperty.call(table, method) ? table[method] : undefined;
    Promise.resolve()
      .then(() => {
        if (handler === undefined) throw new Error(`unknown RPC method: ${method}`);
        return handler(params);
      })
      .then(
        (result: unknown) => {
          const transfer = transferOf(result);
          if (id === 0) return;
          try {
            respond({ t: 'res', id, ok: true, result }, transfer);
          } catch (err) {
            // e.g. DataCloneError: report it instead of leaving the call pending forever.
            respondError(id, method, err);
          }
        },
        (err: unknown) => {
          respondError(id, method, err);
        },
      );
  });
  return {
    emit(event, payload, transfer) {
      const msg: RpcEventMsg = { t: 'evt', event, payload };
      scope.postMessage(msg, transfer === undefined ? [] : [...transfer]);
    },
  };
}

export const createRpcClient: CreateRpcClient = createRpcClientEx;

export const serveRpc: ServeRpc = rpcServer;

// ------------------------------ in-process transport ------------------------------

/** A worker/scope pair connected on the current thread (see the file header). */
export interface InProcessConnection {
  /** Client end: pass to `createRpcClient`. */
  readonly worker: Worker;
  /** Worker end: pass to `serveRpc`. */
  readonly scope: RpcScope;
  /** Simulates an uncaught error in the worker (`error` event on the client end). */
  crash(reason: string): void;
  /** True after `worker.terminate()` (or `close()`). */
  readonly closed: boolean;
  /** Called once when the client terminates the worker. */
  onTerminate(cb: () => void): void;
  close(): void;
}

type Listener = (e: MessageEvent) => void;

/** Ordered asynchronous delivery: one macrotask drains everything queued for a direction. */
function createLane(deliver: (data: unknown) => void, isClosed: () => boolean): (data: unknown) => void {
  const queue: unknown[] = [];
  let scheduled = false;
  const drain = (): void => {
    scheduled = false;
    while (queue.length > 0) {
      const data = queue.shift();
      if (isClosed()) {
        queue.length = 0;
        return;
      }
      deliver(data);
    }
  };
  return (data) => {
    if (isClosed()) return;
    queue.push(structuredClone(data));
    if (!scheduled) {
      scheduled = true;
      setTimeout(drain, 0);
    }
  };
}

export function connectInProcess(): InProcessConnection {
  let closed = false;
  const toWorker = new Set<Listener>();
  const toClient = new Set<Listener>();
  const errorListeners = new Set<(e: ErrorEvent) => void>();
  const terminateCallbacks: Array<() => void> = [];
  const isClosed = (): boolean => closed;
  const deliverTo =
    (set: Set<Listener>) =>
    (data: unknown): void => {
      const event = { data } as MessageEvent;
      for (const cb of [...set]) cb(event);
    };
  const postToWorker = createLane(deliverTo(toWorker), isClosed);
  const postToClient = createLane(deliverTo(toClient), isClosed);

  const close = (): void => {
    if (closed) return;
    closed = true;
    for (const cb of terminateCallbacks.splice(0)) cb();
  };

  const worker = {
    postMessage(msg: unknown, _transfer?: Transferable[]): void {
      postToWorker(msg);
    },
    addEventListener(type: string, cb: (e: never) => void): void {
      if (type === 'message') toClient.add(cb as Listener);
      else if (type === 'error') errorListeners.add(cb as (e: ErrorEvent) => void);
    },
    removeEventListener(type: string, cb: (e: never) => void): void {
      if (type === 'message') toClient.delete(cb as Listener);
      else if (type === 'error') errorListeners.delete(cb as (e: ErrorEvent) => void);
    },
    terminate(): void {
      close();
    },
  } as unknown as Worker;

  const scope: RpcScope = {
    postMessage(msg: unknown, _transfer: Transferable[]): void {
      postToClient(msg);
    },
    addEventListener(_type: 'message', cb: (e: MessageEvent) => void): void {
      toWorker.add(cb);
    },
  };

  return {
    worker,
    scope,
    crash(reason) {
      if (closed) return;
      const event = { message: reason } as ErrorEvent;
      setTimeout(() => {
        if (closed) return;
        for (const cb of [...errorListeners]) cb(event);
      }, 0);
    },
    get closed() {
      return closed;
    },
    onTerminate(cb) {
      if (closed) cb();
      else terminateCallbacks.push(cb);
    },
    close,
  };
}
